import { needsConvertedImagePreview } from '../../../utils/convertedImagePreview';

/** Must match server `MAX_LEAD_DOCUMENT_UPLOAD_BYTES` in config.ts */
export const MAX_LEAD_DOCUMENT_UPLOAD_BYTES = 50 * 1024 * 1024;

export function leadDocumentUploadSizeError(filename: string): string {
  const maxMb = Math.round(MAX_LEAD_DOCUMENT_UPLOAD_BYTES / (1024 * 1024));
  return `${filename}: Datei ist zu groß (max. ${maxMb} MB).`;
}

export type LeadDocument = {
  id: number;
  lead_id: number;
  filename: string;
  mime_type: string | null;
  size: number;
  created_at: string;
  created_by_label?: string | null;
  /** false = steuerrelevantes Rechnungs-PDF — Löschen gesperrt (GoBD). Default/undefined = löschbar. */
  can_delete?: boolean;
  /** Linked to an incoming invoice (Eingangsrechnung) on this project. */
  is_incoming_invoice?: boolean;
  /** Verknüpfter Beleg (Rechnung, Angebot, …) – wählt die Textvorlage für „Per E-Mail senden“. */
  linked_document?: LeadLinkedInvoiceDocument | null;
};

export type LeadLinkedInvoiceDocument = {
  document_type: string;
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  gross_amount: number | null;
  currency: string | null;
};

export function isLeadDocumentDeletable(doc: Pick<LeadDocument, 'can_delete'>): boolean {
  return doc.can_delete !== false;
}

export function leadDocumentHistoryTitle(
  docs: Array<Pick<LeadDocument, 'is_incoming_invoice'>>,
): string {
  const allIncoming = docs.length > 0 && docs.every((doc) => doc.is_incoming_invoice);
  if (allIncoming) {
    return docs.length === 1 ? 'Eingangsrechnung empfangen' : `${docs.length} Eingangsrechnungen empfangen`;
  }
  return docs.length === 1 ? 'Datei hochgeladen' : `${docs.length} Dateien hochgeladen`;
}

export function formatLeadDocumentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
}

export function getFileTypeStyle(filename: string) {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  if (['pdf'].includes(ext)) return { bg: 'bg-red-100', text: 'text-red-700', label: 'PDF' };
  if (['doc', 'docx'].includes(ext)) return { bg: 'bg-blue-100', text: 'text-blue-700', label: ext.toUpperCase() };
  if (['xls', 'xlsx'].includes(ext)) return { bg: 'bg-emerald-100', text: 'text-emerald-700', label: ext.toUpperCase() };
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'dng'].includes(ext)) {
    return { bg: 'bg-amber-100', text: 'text-amber-700', label: ext.toUpperCase() };
  }
  if (['tsx', 'ts', 'jsx', 'js'].includes(ext)) return { bg: 'bg-blue-100', text: 'text-blue-700', label: ext.toUpperCase() };
  if (['html', 'htm'].includes(ext)) return { bg: 'bg-green-100', text: 'text-green-700', label: 'HTML' };
  return { bg: 'bg-app-surface-muted', text: 'text-app-text-muted', label: ext || '—' };
}

export function isImageDocument(doc: LeadDocument): boolean {
  if (isPdfDocument(doc)) return false;
  const ext = doc.filename.split('.').pop()?.toLowerCase() ?? '';
  return (
    (doc.mime_type?.startsWith('image/') ?? false) ||
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'heic', 'heif', 'dng'].includes(ext)
  );
}

export function needsConvertedLeadDocumentPreview(doc: Pick<LeadDocument, 'filename' | 'mime_type'>): boolean {
  return needsConvertedImagePreview(doc.filename, doc.mime_type);
}

export function isPdfDocument(doc: LeadDocument): boolean {
  const ext = doc.filename.split('.').pop()?.toLowerCase() ?? '';
  return doc.mime_type === 'application/pdf' || ext === 'pdf';
}

export function isOtherDocument(doc: LeadDocument): boolean {
  return !isPdfDocument(doc) && !isImageDocument(doc);
}

export function getLeadDocumentUrl(
  leadId: string | number,
  doc: LeadDocument,
  opts?: { download?: boolean },
): string {
  const base = `/api/leads/${leadId}/documents/${doc.id}`;
  if (opts?.download) return `${base}?download=1`;
  return needsConvertedLeadDocumentPreview(doc) ? `${base}?preview=1` : base;
}

export function partitionLeadDocuments(docs: LeadDocument[]): {
  pdfs: LeadDocument[];
  images: LeadDocument[];
  other: LeadDocument[];
} {
  const pdfs: LeadDocument[] = [];
  const images: LeadDocument[] = [];
  const other: LeadDocument[] = [];
  for (const doc of docs) {
    if (isPdfDocument(doc)) pdfs.push(doc);
    else if (isImageDocument(doc)) images.push(doc);
    else other.push(doc);
  }
  return { pdfs, images, other };
}
